import type { AppLocale } from '../messages';

/**
 * Строки участка «chatList». Свой файл у каждого участка, а не общий `messages.ts` на 4300 строк:
 * участки правятся параллельно, и общий файл давал бы конфликт на каждой новой строке.
 * Ключ добавляется во все четыре словаря — `en` задаёт набор, остальные проверяются типом.
 *
 * <p>Терминология (аудит удобства 2026-10-04): в рабочих подписях — «контакты», без «хирда»,
 * «команды» и «дружины». Варяжские слова остаются только в оформлении темы.
 */
const en = {
  // Список чатов
  chatListComposeA11y: 'New message',
  chatListSearchPlaceholder: 'Search chats',
  chatListSearchClearA11y: 'Clear search',
  chatListSearchPeople: 'Search people for “{query}”',
  chatListSearchNothing: 'No chats match “{query}”',
  chatListFilterAll: 'All',
  chatListFilterPersonal: 'Personal',
  chatListSortA11y: 'Sort order: {value}',
  chatListSortTitle: 'Sort chats',
  chatListRequestsBanner: 'Contact requests: {count}',
  chatListRequestsBannerA11y: 'Contact requests: {count}. Open',
  chatListChannelCatalog: 'Browse the channel catalog',
  chatListEmptyTitle: 'Start your first conversation',
  chatListEmptyBody:
    'Find people by name or @handle and send a contact request. Once they accept it, you can write and call each other.',
  chatListEmptyFindPeople: 'Find people',
  chatListEmptyInvite: 'Invite by link',
  chatListLoadFailed: 'Could not load chats',
  chatListMenuA11y: 'Chat actions',
  chatListMuted: 'Muted',
  chatListMuteAction: 'Mute notifications…',
  chatListMute1h: 'Mute for 1 hour',
  chatListMute8h: 'Mute for 8 hours',
  chatListMuteForever: 'Mute until I turn it back on',
  chatListMuteCallsHint: 'Calls still ring when a chat is muted.',
  chatListUnmute: 'Unmute',
  chatListMutedToast1h: 'Muted for 1 hour',
  chatListMutedToast8h: 'Muted for 8 hours',
  chatListMutedToastForever: 'Chat muted',
  chatListUnmutedToast: 'Notifications are on',
  chatListMuteFailed: 'Could not change notifications',
  chatListMarkRead: 'Mark as read',
  chatListDeleteChat: 'Delete chat',
  chatListDeleteForMeTitle: 'Delete the chat with {name}?',
  chatListDeleteForMeBody: 'The chat disappears from your list. It comes back if {name} writes again.',
  chatListDeleteAlsoFor: 'Also delete for {name}',
  chatListDeleteAlsoForHint: 'The whole history will be erased for both of you.',
  chatListDeleteConfirm: 'Delete',
  chatListDeleteGroup: 'Delete group for everyone',
  chatListDeleteGroupBody: 'The group and all its messages will be deleted for every member.',
  chatListDeletedToast: 'Chat deleted',
  chatListLeftToast: 'You left the group',
  chatListUnsubscribedToast: 'Unsubscribed',
  // «Новое сообщение»
  newMessageTitle: 'New message',
  chatPaneEmptyTitle: 'Select a chat',
  chatPaneEmptyHint: 'Pick a conversation on the left or start a new one.',
  newMessageSearchPlaceholder: 'Name or @handle',
  newMessageSearchA11y: 'Search contacts and people',
  newMessageNewGroup: 'New group',
  newMessageNewChannel: 'New channel',
  newMessageAddContact: 'Add contact',
  newMessageAddContactHint: 'Type a name or @handle in the field above',
  newMessageInvite: 'Invite by link or QR',
  newMessageChannelCatalog: 'Channel catalog',
  newMessageRequestsTitle: 'Requests ({count})',
  newMessageRequestIncoming: 'Wants to add you',
  newMessageAccept: 'Accept',
  newMessageAcceptA11y: 'Accept request from {name}',
  newMessageDeclineA11y: 'Decline request from {name}',
  newMessageCancelRequest: 'Cancel',
  newMessageCancelRequestA11y: 'Cancel request to {name}',
  newMessageDeclinedToast: 'Request declined',
  newMessageAcceptedToast: '{name} is now in your contacts',
  newMessageRequestSentToast: 'Request sent to {name}',
  newMessageRequestCancelledToast: 'Request cancelled',
  newMessageContactsTitle: 'Contacts',
  newMessageContactsEmpty: 'No contacts yet. Find people by name or @handle, or send them your invite link.',
  newMessageContactsLoadFailed: 'Could not load contacts.',
  newMessagePeopleTitle: 'People',
  newMessageAdd: 'Add',
  newMessageMessage: 'Message',
  newMessageRequestSent: 'Request sent',
  newMessageOpenProfileA11y: 'Profile of {name}',
  // Приглашение
  inviteTitle: 'Invite to SVYAZ',
  inviteBody: 'Anyone with this link can find you and send you a contact request.',
  inviteNoHandleHint:
    'You have no public @handle yet, so this is a general link to the app. Set a handle in Settings → Profile so friends can find you directly.',
  inviteShare: 'Share link',
  inviteCopy: 'Copy link',
  inviteCopied: 'Link copied',
  inviteMessage: 'Join me on SVYAZ: {url}',
  inviteQrA11y: 'QR code with your invite link',
  // Новая группа
  newGroupNext: 'Next',
  newGroupBack: 'Back',
  newGroupSelected: 'Selected: {count}',
  newGroupRemoveChipA11y: 'Remove {name}',
  newGroupNameTitle: 'Name the group',
  newGroupNameHint: 'Enter a name to create the group.',
  newGroupMembersSummary: 'Members: {count}',
  newGroupPickA11y: 'Select {name}',
  // Карточка профиля
  profileMessage: 'Message',
  profileCall: 'Call',
  profileVideo: 'Video',
  profileMore: 'More actions',
  profileShareLink: 'Share profile link',
  profileRemoveContact: 'Remove from contacts',
  profileRemoveConfirmTitle: 'Remove {name} from contacts?',
  profileRemovedToast: 'Removed from contacts',
  // Приглашение от BOSS (PRIV-2): срок очистки личных чатов — с сервера, не зашитый.
  bossInviteRetentionEvery:
    '\n• In direct (non-group) chats the server erases the whole message history {days, plural, =1 {every day} other {every # days}}, so long threads are not kept forever.',
  bossInviteRetentionPeriodic:
    '\n• In direct (non-group) chats the server periodically erases the message history, so long threads are not kept forever.',
} as const satisfies Record<string, string>;

export type ChatListMessageKey = keyof typeof en;

export const chatListMessages: Record<AppLocale, Record<ChatListMessageKey, string>> = {
  en,
  ru: {
    chatListComposeA11y: 'Новое сообщение',
    chatListSearchPlaceholder: 'Поиск по чатам',
    chatListSearchClearA11y: 'Очистить поиск',
    chatListSearchPeople: 'Найти людей по запросу «{query}»',
    chatListSearchNothing: 'Нет чатов по запросу «{query}»',
    chatListFilterAll: 'Все',
    chatListFilterPersonal: 'Личные',
    chatListSortA11y: 'Порядок: {value}',
    chatListSortTitle: 'Порядок чатов',
    chatListRequestsBanner: 'Заявки в контакты: {count}',
    chatListRequestsBannerA11y: 'Заявки в контакты: {count}. Открыть',
    chatListChannelCatalog: 'Открыть каталог каналов',
    chatListEmptyTitle: 'Начните первый разговор',
    chatListEmptyBody:
      'Найдите человека по имени или @нику и отправьте заявку в контакты. Когда он её примет, вы сможете переписываться и созваниваться.',
    chatListEmptyFindPeople: 'Найти людей',
    chatListEmptyInvite: 'Пригласить по ссылке',
    chatListLoadFailed: 'Не удалось загрузить чаты',
    chatListMenuA11y: 'Действия с чатом',
    chatListMuted: 'Без звука',
    chatListMuteAction: 'Отключить звук…',
    chatListMute1h: 'Без звука на 1 час',
    chatListMute8h: 'Без звука на 8 часов',
    chatListMuteForever: 'Без звука, пока не включу',
    chatListMuteCallsHint: 'Звонки в заглушённом чате всё равно звонят.',
    chatListUnmute: 'Включить звук',
    chatListMutedToast1h: 'Звук выключен на 1 час',
    chatListMutedToast8h: 'Звук выключен на 8 часов',
    chatListMutedToastForever: 'Звук чата выключен',
    chatListUnmutedToast: 'Уведомления включены',
    chatListMuteFailed: 'Не удалось изменить уведомления',
    chatListMarkRead: 'Отметить прочитанным',
    chatListDeleteChat: 'Удалить чат',
    chatListDeleteForMeTitle: 'Удалить чат с {name}?',
    chatListDeleteForMeBody: 'Чат пропадёт из вашего списка. Он вернётся, если {name} напишет снова.',
    chatListDeleteAlsoFor: 'Удалить и у {name}',
    chatListDeleteAlsoForHint: 'Вся переписка сотрётся у вас обоих.',
    chatListDeleteConfirm: 'Удалить',
    chatListDeleteGroup: 'Удалить группу у всех',
    chatListDeleteGroupBody: 'Группа и все её сообщения будут удалены у всех участников.',
    chatListDeletedToast: 'Чат удалён',
    chatListLeftToast: 'Вы вышли из группы',
    chatListUnsubscribedToast: 'Вы отписались',
    newMessageTitle: 'Новое сообщение',
    chatPaneEmptyTitle: 'Выберите чат',
    chatPaneEmptyHint: 'Откройте переписку слева или начните новую.',
    newMessageSearchPlaceholder: 'Имя или @ник',
    newMessageSearchA11y: 'Поиск по контактам и людям',
    newMessageNewGroup: 'Новая группа',
    newMessageNewChannel: 'Новый канал',
    newMessageAddContact: 'Добавить контакт',
    newMessageAddContactHint: 'Введите имя или @ник в поле выше',
    newMessageInvite: 'Пригласить по ссылке или QR',
    newMessageChannelCatalog: 'Каталог каналов',
    newMessageRequestsTitle: 'Заявки ({count})',
    newMessageRequestIncoming: 'Хочет добавить вас',
    newMessageAccept: 'Принять',
    newMessageAcceptA11y: 'Принять заявку от {name}',
    newMessageDeclineA11y: 'Отклонить заявку от {name}',
    newMessageCancelRequest: 'Отозвать',
    newMessageCancelRequestA11y: 'Отозвать заявку для {name}',
    newMessageDeclinedToast: 'Заявка отклонена',
    newMessageAcceptedToast: '{name} теперь в ваших контактах',
    newMessageRequestSentToast: 'Заявка отправлена: {name}',
    newMessageRequestCancelledToast: 'Заявка отозвана',
    newMessageContactsTitle: 'Контакты',
    newMessageContactsEmpty:
      'Контактов пока нет. Найдите людей по имени или @нику или отправьте им ссылку-приглашение.',
    newMessageContactsLoadFailed: 'Не удалось загрузить контакты.',
    newMessagePeopleTitle: 'Люди',
    newMessageAdd: 'Добавить',
    newMessageMessage: 'Написать',
    newMessageRequestSent: 'Заявка отправлена',
    newMessageOpenProfileA11y: 'Профиль: {name}',
    inviteTitle: 'Пригласить в SVYAZ',
    inviteBody: 'По этой ссылке вас найдут и смогут отправить заявку в контакты.',
    inviteNoHandleHint:
      'У вас пока нет публичного @ника, поэтому это общая ссылка на приложение. Задайте ник в «Настройках» → «Профиль», чтобы друзья находили вас сразу.',
    inviteShare: 'Поделиться ссылкой',
    inviteCopy: 'Скопировать ссылку',
    inviteCopied: 'Ссылка скопирована',
    inviteMessage: 'Пишите мне в SVYAZ: {url}',
    inviteQrA11y: 'QR-код с вашей ссылкой-приглашением',
    newGroupNext: 'Далее',
    newGroupBack: 'Назад',
    newGroupSelected: 'Выбрано: {count}',
    newGroupRemoveChipA11y: 'Убрать: {name}',
    newGroupNameTitle: 'Название группы',
    newGroupNameHint: 'Введите название, чтобы создать группу.',
    newGroupMembersSummary: 'Участников: {count}',
    newGroupPickA11y: 'Выбрать: {name}',
    profileMessage: 'Написать',
    profileCall: 'Позвонить',
    profileVideo: 'Видео',
    profileMore: 'Ещё действия',
    profileShareLink: 'Поделиться ссылкой на профиль',
    profileRemoveContact: 'Удалить из контактов',
    profileRemoveConfirmTitle: 'Удалить {name} из контактов?',
    profileRemovedToast: 'Удалено из контактов',
    bossInviteRetentionEvery:
      '\n• В личных (не групповых) чатах сервер стирает всю историю сообщений {days, plural, =1 {каждый день} one {раз в # день} few {раз в # дня} many {раз в # дней} other {раз в # дня}}, чтобы длинная переписка не хранилась бесконечно.',
    bossInviteRetentionPeriodic:
      '\n• В личных (не групповых) чатах сервер периодически стирает историю сообщений, чтобы длинная переписка не хранилась бесконечно.',
  },
  sr: {
    chatListComposeA11y: 'Nova poruka',
    chatListSearchPlaceholder: 'Pretraga četova',
    chatListSearchClearA11y: 'Obriši pretragu',
    chatListSearchPeople: 'Traži ljude za „{query}”',
    chatListSearchNothing: 'Nema četova za „{query}”',
    chatListFilterAll: 'Sve',
    chatListFilterPersonal: 'Lični',
    chatListSortA11y: 'Redosled: {value}',
    chatListSortTitle: 'Redosled četova',
    chatListRequestsBanner: 'Zahtevi za kontakt: {count}',
    chatListRequestsBannerA11y: 'Zahtevi za kontakt: {count}. Otvori',
    chatListChannelCatalog: 'Otvori katalog kanala',
    chatListEmptyTitle: 'Započnite prvi razgovor',
    chatListEmptyBody:
      'Pronađite osobu po imenu ili @nadimku i pošaljite zahtev za kontakt. Kada ga prihvati, možete se dopisivati i pozivati.',
    chatListEmptyFindPeople: 'Pronađi ljude',
    chatListEmptyInvite: 'Pozovi linkom',
    chatListLoadFailed: 'Četovi se nisu učitali',
    chatListMenuA11y: 'Radnje sa četom',
    chatListMuted: 'Bez zvuka',
    chatListMuteAction: 'Isključi zvuk…',
    chatListMute1h: 'Bez zvuka 1 sat',
    chatListMute8h: 'Bez zvuka 8 sati',
    chatListMuteForever: 'Bez zvuka dok ne uključim',
    chatListMuteCallsHint: 'Pozivi i dalje zvone u utišanom četu.',
    chatListUnmute: 'Uključi zvuk',
    chatListMutedToast1h: 'Zvuk isključen na 1 sat',
    chatListMutedToast8h: 'Zvuk isključen na 8 sati',
    chatListMutedToastForever: 'Zvuk četa je isključen',
    chatListUnmutedToast: 'Obaveštenja su uključena',
    chatListMuteFailed: 'Obaveštenja se nisu promenila',
    chatListMarkRead: 'Označi kao pročitano',
    chatListDeleteChat: 'Obriši čet',
    chatListDeleteForMeTitle: 'Obrisati čet sa {name}?',
    chatListDeleteForMeBody: 'Čet nestaje sa vaše liste. Vratiće se ako {name} ponovo piše.',
    chatListDeleteAlsoFor: 'Obriši i za {name}',
    chatListDeleteAlsoForHint: 'Cela prepiska biće obrisana za oboje.',
    chatListDeleteConfirm: 'Obriši',
    chatListDeleteGroup: 'Obriši grupu za sve',
    chatListDeleteGroupBody: 'Grupa i sve njene poruke biće obrisane za sve članove.',
    chatListDeletedToast: 'Čet je obrisan',
    chatListLeftToast: 'Napustili ste grupu',
    chatListUnsubscribedToast: 'Otkazali ste praćenje',
    newMessageTitle: 'Nova poruka',
    chatPaneEmptyTitle: 'Izaberite čet',
    chatPaneEmptyHint: 'Otvorite prepisku levo ili započnite novu.',
    newMessageSearchPlaceholder: 'Ime ili @nadimak',
    newMessageSearchA11y: 'Pretraga kontakata i ljudi',
    newMessageNewGroup: 'Nova grupa',
    newMessageNewChannel: 'Novi kanal',
    newMessageAddContact: 'Dodaj kontakt',
    newMessageAddContactHint: 'Unesite ime ili @nadimak u polje iznad',
    newMessageInvite: 'Pozovi linkom ili QR kodom',
    newMessageChannelCatalog: 'Katalog kanala',
    newMessageRequestsTitle: 'Zahtevi ({count})',
    newMessageRequestIncoming: 'Želi da vas doda',
    newMessageAccept: 'Prihvati',
    newMessageAcceptA11y: 'Prihvati zahtev od {name}',
    newMessageDeclineA11y: 'Odbij zahtev od {name}',
    newMessageCancelRequest: 'Povuci',
    newMessageCancelRequestA11y: 'Povuci zahtev za {name}',
    newMessageDeclinedToast: 'Zahtev odbijen',
    newMessageAcceptedToast: '{name} je sada u vašim kontaktima',
    newMessageRequestSentToast: 'Zahtev poslat: {name}',
    newMessageRequestCancelledToast: 'Zahtev povučen',
    newMessageContactsTitle: 'Kontakti',
    newMessageContactsEmpty:
      'Još nemate kontakte. Pronađite ljude po imenu ili @nadimku ili im pošaljite link za poziv.',
    newMessageContactsLoadFailed: 'Kontakti se nisu učitali.',
    newMessagePeopleTitle: 'Ljudi',
    newMessageAdd: 'Dodaj',
    newMessageMessage: 'Piši',
    newMessageRequestSent: 'Zahtev poslat',
    newMessageOpenProfileA11y: 'Profil: {name}',
    inviteTitle: 'Pozovi u SVYAZ',
    inviteBody: 'Preko ovog linka svako može da vas pronađe i pošalje zahtev za kontakt.',
    inviteNoHandleHint:
      'Još nemate javni @nadimak, pa je ovo opšti link ka aplikaciji. Postavite nadimak u „Podešavanja” → „Profil” da bi vas prijatelji odmah pronašli.',
    inviteShare: 'Podeli link',
    inviteCopy: 'Kopiraj link',
    inviteCopied: 'Link je kopiran',
    inviteMessage: 'Pišite mi u SVYAZ: {url}',
    inviteQrA11y: 'QR kod sa vašim linkom za poziv',
    newGroupNext: 'Dalje',
    newGroupBack: 'Nazad',
    newGroupSelected: 'Izabrano: {count}',
    newGroupRemoveChipA11y: 'Ukloni: {name}',
    newGroupNameTitle: 'Naziv grupe',
    newGroupNameHint: 'Unesite naziv da biste napravili grupu.',
    newGroupMembersSummary: 'Članova: {count}',
    newGroupPickA11y: 'Izaberi: {name}',
    profileMessage: 'Piši',
    profileCall: 'Pozovi',
    profileVideo: 'Video poziv',
    profileMore: 'Još radnji',
    profileShareLink: 'Podeli link profila',
    profileRemoveContact: 'Ukloni iz kontakata',
    profileRemoveConfirmTitle: 'Ukloniti {name} iz kontakata?',
    profileRemovedToast: 'Uklonjeno iz kontakata',
    bossInviteRetentionEvery:
      '\n• U ličnim (negrupnim) četovima server briše celu istoriju poruka {days, plural, =1 {svaki dan} one {jednom u # dan} few {jednom u # dana} other {jednom u # dana}}, da duga prepiska ne ostane zauvek.',
    bossInviteRetentionPeriodic:
      '\n• U ličnim (negrupnim) četovima server povremeno briše istoriju poruka, da duga prepiska ne ostane zauvek.',
  },
  fr: {
    chatListComposeA11y: 'Nouveau message',
    chatListSearchPlaceholder: 'Rechercher une discussion',
    chatListSearchClearA11y: 'Effacer la recherche',
    chatListSearchPeople: 'Chercher des personnes : « {query} »',
    chatListSearchNothing: 'Aucune discussion pour « {query} »',
    chatListFilterAll: 'Toutes',
    chatListFilterPersonal: 'Privées',
    chatListSortA11y: 'Ordre : {value}',
    chatListSortTitle: 'Ordre des discussions',
    chatListRequestsBanner: 'Demandes de contact : {count}',
    chatListRequestsBannerA11y: 'Demandes de contact : {count}. Ouvrir',
    chatListChannelCatalog: 'Parcourir le catalogue des chaînes',
    chatListEmptyTitle: 'Commencez votre première conversation',
    chatListEmptyBody:
      'Trouvez quelqu’un par son nom ou son @pseudo et envoyez une demande de contact. Une fois acceptée, vous pourrez vous écrire et vous appeler.',
    chatListEmptyFindPeople: 'Trouver des personnes',
    chatListEmptyInvite: 'Inviter par lien',
    chatListLoadFailed: 'Impossible de charger les discussions',
    chatListMenuA11y: 'Actions sur la discussion',
    chatListMuted: 'Silencieux',
    chatListMuteAction: 'Mettre en silencieux…',
    chatListMute1h: 'Silencieux pendant 1 heure',
    chatListMute8h: 'Silencieux pendant 8 heures',
    chatListMuteForever: 'Silencieux jusqu’à réactivation',
    chatListMuteCallsHint: 'Les appels sonnent même dans une discussion silencieuse.',
    chatListUnmute: 'Réactiver le son',
    chatListMutedToast1h: 'Silencieux pendant 1 heure',
    chatListMutedToast8h: 'Silencieux pendant 8 heures',
    chatListMutedToastForever: 'Discussion en silencieux',
    chatListUnmutedToast: 'Notifications activées',
    chatListMuteFailed: 'Impossible de modifier les notifications',
    chatListMarkRead: 'Marquer comme lu',
    chatListDeleteChat: 'Supprimer la discussion',
    chatListDeleteForMeTitle: 'Supprimer la discussion avec {name} ?',
    chatListDeleteForMeBody: 'La discussion disparaît de votre liste. Elle reviendra si {name} vous écrit à nouveau.',
    chatListDeleteAlsoFor: 'Supprimer aussi pour {name}',
    chatListDeleteAlsoForHint: 'Tout l’historique sera effacé pour vous deux.',
    chatListDeleteConfirm: 'Supprimer',
    chatListDeleteGroup: 'Supprimer le groupe pour tous',
    chatListDeleteGroupBody: 'Le groupe et tous ses messages seront supprimés pour tous les membres.',
    chatListDeletedToast: 'Discussion supprimée',
    chatListLeftToast: 'Vous avez quitté le groupe',
    chatListUnsubscribedToast: 'Désabonnement effectué',
    newMessageTitle: 'Nouveau message',
    chatPaneEmptyTitle: 'Choisissez une discussion',
    chatPaneEmptyHint: 'Ouvrez une conversation à gauche ou commencez-en une nouvelle.',
    newMessageSearchPlaceholder: 'Nom ou @pseudo',
    newMessageSearchA11y: 'Rechercher des contacts et des personnes',
    newMessageNewGroup: 'Nouveau groupe',
    newMessageNewChannel: 'Nouvelle chaîne',
    newMessageAddContact: 'Ajouter un contact',
    newMessageAddContactHint: 'Saisissez un nom ou un @pseudo dans le champ ci-dessus',
    newMessageInvite: 'Inviter par lien ou QR',
    newMessageChannelCatalog: 'Catalogue des chaînes',
    newMessageRequestsTitle: 'Demandes ({count})',
    newMessageRequestIncoming: 'Souhaite vous ajouter',
    newMessageAccept: 'Accepter',
    newMessageAcceptA11y: 'Accepter la demande de {name}',
    newMessageDeclineA11y: 'Refuser la demande de {name}',
    newMessageCancelRequest: 'Retirer',
    newMessageCancelRequestA11y: 'Retirer la demande envoyée à {name}',
    newMessageDeclinedToast: 'Demande refusée',
    newMessageAcceptedToast: '{name} fait désormais partie de vos contacts',
    newMessageRequestSentToast: 'Demande envoyée : {name}',
    newMessageRequestCancelledToast: 'Demande retirée',
    newMessageContactsTitle: 'Vos contacts',
    newMessageContactsEmpty:
      'Pas encore de contacts. Trouvez des personnes par nom ou @pseudo, ou envoyez-leur votre lien d’invitation.',
    newMessageContactsLoadFailed: 'Impossible de charger les contacts.',
    newMessagePeopleTitle: 'Personnes',
    newMessageAdd: 'Ajouter',
    newMessageMessage: 'Écrire',
    newMessageRequestSent: 'Demande envoyée',
    newMessageOpenProfileA11y: 'Profil de {name}',
    inviteTitle: 'Inviter sur SVYAZ',
    inviteBody: 'Avec ce lien, n’importe qui peut vous trouver et vous envoyer une demande de contact.',
    inviteNoHandleHint:
      'Vous n’avez pas encore de @pseudo public : voici donc un lien général vers l’application. Définissez un pseudo dans Réglages → Profil pour que vos amis vous trouvent directement.',
    inviteShare: 'Partager le lien',
    inviteCopy: 'Copier le lien',
    inviteCopied: 'Lien copié',
    inviteMessage: 'Rejoignez-moi sur SVYAZ : {url}',
    inviteQrA11y: 'QR code de votre lien d’invitation',
    newGroupNext: 'Suivant',
    newGroupBack: 'Retour',
    newGroupSelected: 'Sélectionnés : {count}',
    newGroupRemoveChipA11y: 'Retirer {name}',
    newGroupNameTitle: 'Nom du groupe',
    newGroupNameHint: 'Saisissez un nom pour créer le groupe.',
    newGroupMembersSummary: 'Membres : {count}',
    newGroupPickA11y: 'Sélectionner {name}',
    profileMessage: 'Écrire',
    profileCall: 'Appeler',
    profileVideo: 'Vidéo',
    profileMore: 'Plus d’actions',
    profileShareLink: 'Partager le lien du profil',
    profileRemoveContact: 'Retirer des contacts',
    profileRemoveConfirmTitle: 'Retirer {name} des contacts ?',
    profileRemovedToast: 'Retiré des contacts',
    bossInviteRetentionEvery:
      '\n• Dans les discussions directes (hors groupes), le serveur efface tout l’historique des messages {days, plural, =1 {chaque jour} other {tous les # jours}}, pour ne pas conserver indéfiniment de longs fils.',
    bossInviteRetentionPeriodic:
      '\n• Dans les discussions directes (hors groupes), le serveur efface régulièrement l’historique des messages, pour ne pas conserver indéfiniment de longs fils.',
  },
};
